// =============================================================================
// PROTECTED ROUTE COMPONENT
// Redirects to login if user is not authenticated.
//
// Role checking is multi-source by design: in a multi-tenant app the right
// source of truth for "owner / admin" is the per-tenant `tenant_members.role`
// (loaded by TenantContext), not the global `profiles.role`. The latter
// pre-dates multi-tenancy and is fragile under network conditions because
// the AuthContext profile fetch can time out and silently fall back to a
// restricted 'client' role. We check BOTH sources so a tenant owner keeps
// access even when the profile fetch is slow / fails / times out.
// =============================================================================

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string | null;
}

const ADMIN_TENANT_ROLES = new Set(['owner', 'admin']);

export default function ProtectedRoute({ children, requiredRole = null }: ProtectedRouteProps) {
  const { user, profile, loading, isAdmin, error: authError } = useAuth();
  const { role: tenantRole, isPlatformAdmin, isLoading: tenantLoading, error: tenantError } = useTenant();
  const location = useLocation();
  const isBliss = /(^|\.)blissbunkbeds\.com$/i.test(window.location.hostname);
  const surface = isBliss ? {background: '#faf8f1', color: '#303729'} : {};


  // Show loading spinner while checking auth
  if (loading || (!!user && !!requiredRole && tenantLoading)) {
    return (
      <div className="auth-loading" role="status" style={surface}>
        <div className="auth-loading-spinner"></div>
        <p>Loading your workspace…</p>
        <style>{`
          .auth-loading {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #102332;
            color: #fff;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .auth-loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(var(--color-gold-rgb), 0.2);
            border-top-color: var(--color-gold);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // A failed verification is different from a completed permission denial.
  if ((!user && authError) || (requiredRole && (tenantError || (profile?._fallback && !tenantRole && !isPlatformAdmin)))) {
    return (
      <div role="alert" style={{ minHeight: '100vh', display: 'grid', placeContent: 'center',
        textAlign: 'center', background: '#102332', color: '#fff', padding: 24, ...surface }}>
        <h1>Unable to verify workspace access</h1>
        <p>Please reload to try again.</p>
        <button style={isBliss ? {background: '#536341', color: '#fffdf8', border: 0, borderRadius: 8, padding: '12px 24px', cursor: 'pointer'} : {}} onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for required role.
  // Effective access = (auth-context decision) OR (tenant-context decision).
  // When requiredRole === 'admin', a tenant owner/admin satisfies the check
  // even if profiles.role is missing/stale due to a timeout fallback.
  if (requiredRole) {
    const isAdminFromAuth = isAdmin();
    const isAdminFromTenant =
      tenantRole != null && ADMIN_TENANT_ROLES.has(tenantRole);

    const hasAccess =
      requiredRole === 'admin'
        ? (isAdminFromAuth || isAdminFromTenant || isPlatformAdmin)
        : (profile?.role === requiredRole || tenantRole === requiredRole);

    if (!hasAccess) {
      return (
        <div className="auth-denied" style={surface}>
          <h1 style={isBliss ? {color: '#303729'} : {}}>Access Denied</h1>
          <p>You don't have permission to access this page.</p>
          <a href="/" style={isBliss ? {color: '#536341'} : {}}>Go to Home</a>
          <style>{`
            .auth-denied {
              min-height: 100vh;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: #102332;
              color: #fff;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              text-align: center;
            }
            .auth-denied h1 {
              color: var(--color-gold);
              margin-bottom: 16px;
            }
            .auth-denied a {
              margin-top: 24px;
              color: var(--color-gold);
              text-decoration: none;
            }
            .auth-denied a:hover {
              text-decoration: underline;
            }
          `}</style>
        </div>
      );
    }
  }

  return <>{children}</>;
}


